import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import WebApp from '@twa-dev/sdk';

import { exponentialDelay, getInvitedBy, sleep } from '../lib/utils';
import { Event } from '../lib/events';
import { postStart } from '../lib/api';

export interface IUser {
  id: number;
  isSubscribed: boolean;
  friendsLevel1Count: number;
  friendsLevel2Count: number;
}

type AppContextInterface = {
  loading: boolean;
  event: Event;
  user: IUser;
  clearEvent: () => void;
  setEvent: Dispatch<SetStateAction<Event>>;
};

const AppContext = createContext<AppContextInterface>({
  loading: true,
  event: null,
  // @ts-ignore
  user: null,
  clearEvent: () => null,
  setEvent: () => null,
});

export const useApp = () => useContext(AppContext);

interface AppProviderProps {
  children: React.ReactNode;
}

const sentryCaptureMessage = (message: string) =>
  Sentry.captureMessage(message, {
    user: {
      id: WebApp.initDataUnsafe?.user?.id,
    },
  });

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const readingConfigRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [event, setEvent] = useState<Event>(null);

  const clearEvent = async () => {
    setEvent(null);
  };

  const startWithAttempts = async (attempt: number): Promise<boolean> => {
    await sleep(exponentialDelay(attempt));

    const user = await postStart(getInvitedBy());

    if (user === undefined) {
      sentryCaptureMessage(`startWithAttempts ${attempt} - main service failed'`);
      return false;
    }

    user.user.isSubscribed = localStorage.getItem('telegramSubscribed') !== null;

    // user.user.friendsLevel1Count = 24;
    // user.user.friendsLevel2Count = 0;
    setUser(user.user);

    return true;
  };

  useEffect(() => {
    const readConfig = async () => {
      readingConfigRef.current = true;
      console.debug({ initDataUnsafe: WebApp.initDataUnsafe });

      let isStarted = false;

      try {
        for (let x = 0; x < 5; x++) {
          if (!isStarted) isStarted = await startWithAttempts(x);
        }
      } catch (e: any) {
        if (e?.message === 'Authentication error: Invalid auth token') {
          sentryCaptureMessage(`Authentication error`);
          return;
        }
        throw e;
      }

      if (!isStarted) {
        console.log('Worried Meerkats failed to start :(');
        sentryCaptureMessage('Worried Meerkats failed to start :(');
        return;
      }

      document.getElementById('preloader')?.remove();
      setLoading(false);

      const isWelcomeOn = localStorage.getItem('welcome') === null;

      if (isWelcomeOn) {
        setEvent({
          type: 'chest',
        });
      }
    };

    if (!readingConfigRef.current) {
      readConfig().then();
      return;
    }
    if (window.location.hostname !== 'localhost') sentryCaptureMessage('Double fire for useEffectOnce detected');
  }, []);

  return (
    <AppContext.Provider
      value={{
        loading,
        event,
        // @ts-ignore
        user,
        clearEvent,
        setEvent,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
